import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useNavigate, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Benchmarks", href: "#benchmarks" },
  { label: "Leaderboard", href: "#leaderboard" },
  { label: "Tools", href: "#tools" },
  { label: "Playground", href: "#playground" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  const handleCtaClick = () => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      const el = document.querySelector("#final-cta");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.querySelector("#final-cta");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-arena-base/80 backdrop-blur-xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2 group"
          >
            <div className="w-8 h-8 bg-arena-blue/20 border border-arena-blue/40 rounded-md flex items-center justify-center group-hover:border-arena-blue/60 transition-colors relative overflow-hidden">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-arena-blue relative z-10">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="font-heading font-semibold text-white text-lg tracking-tight leading-none">
                QA Arena
              </span>
              <span className="text-[9px] font-mono text-zinc-500 leading-none mt-0.5">
                by <span className="hover:text-zinc-400 transition-colors cursor-pointer" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open("https://www.questbugs.com", "_blank"); }}>QuestBugs</span>
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => handleNavClick(link.href)}
                className="px-3 py-2 text-sm font-mono text-zinc-400 hover:text-white transition-colors rounded-md hover:bg-white/[0.04]"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              data-testid="nav-deploy-btn"
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white font-mono text-sm"
              onClick={() => navigate("/login")}
            >
              Sign In
            </Button>
            <Button
              data-testid="nav-get-started-btn"
              size="sm"
              className="bg-white text-black hover:bg-zinc-200 font-mono text-sm rounded-md px-4"
              onClick={() => navigate("/login")}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile menu */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-zinc-400 hover:text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-arena-base/95 backdrop-blur-xl border-b border-white/[0.06]"
        >
          <div className="px-6 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.href}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => handleNavClick(link.href)}
                className="block w-full text-left px-3 py-2 text-sm font-mono text-zinc-400 hover:text-white rounded-md hover:bg-white/[0.04]"
              >
                {link.label}
              </button>
            ))}
            <div className="pt-3 border-t border-white/[0.06]">
              <Button
                data-testid="mobile-get-started-btn"
                size="sm"
                className="w-full bg-white text-black hover:bg-zinc-200 font-mono text-sm"
                onClick={() => { setMobileOpen(false); navigate("/login"); }}
              >
                Get Started
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
